import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  FileText,
  Mail,
  Download,
  Loader2,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Send,
  Eye
} from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const getHealthColor = (score) => {
  if (score >= 80) return 'text-green-600 bg-green-100';
  if (score >= 50) return 'text-amber-600 bg-amber-100';
  return 'text-red-600 bg-red-100';
};

const getHealthStatus = (score) => {
  if (score >= 80) return 'Healthy';
  if (score >= 50) return 'Caution';
  return 'At Risk';
};

export const HealthReports = () => {
  const [trademarks, setTrademarks] = useState([]);
  const [selectedTrademark, setSelectedTrademark] = useState('all');
  const [sendEmail, setSendEmail] = useState(false);
  const [useTestEmail, setUseTestEmail] = useState(true);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [lastResults, setLastResults] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tmRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/trademarks`),
        axios.get(`${API}/health-reports/schedule-status`)
      ]);
      setTrademarks(tmRes.data);
      setScheduleStatus(scheduleRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReports = async () => {
    setGenerating(true);
    setLastResults(null);
    
    try {
      const response = await axios.post(`${API}/health-reports/generate`, {
        trademark_id: selectedTrademark === 'all' ? null : selectedTrademark,
        send_email: sendEmail,
        use_test_email: useTestEmail
      });
      
      setLastResults(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error generating reports:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate reports');
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewReport = async (trademarkId) => {
    try {
      const response = await axios.get(`${API}/health-reports/preview/${trademarkId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Health_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  const handleSendTestEmail = async (trademarkId) => {
    try {
      const response = await axios.post(`${API}/health-reports/send-test?trademark_id=${trademarkId}`);
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="health-reports-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="health-reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
          Health Check Reports
        </h1>
        <p className="text-slate-500 mt-1">
          Generate and send monthly trade mark health reports to clients
        </p>
      </div>

      {/* Schedule Status */}
      <motion.div 
        className="swiss-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg text-slate-900">Automatic Schedule</h2>
            <p className="text-sm text-slate-500">Monthly reports are sent automatically</p>
          </div>
        </div>

        {scheduleStatus && (
          <div className="bg-slate-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              {scheduleStatus.scheduled ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              )}
              <span className="font-semibold text-slate-900">
                {scheduleStatus.scheduled ? 'Scheduler Active' : 'Scheduler Inactive'}
              </span>
            </div>
            {scheduleStatus.scheduled && (
              <>
                <p className="text-sm text-slate-600 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  {scheduleStatus.schedule}
                </p>
                {scheduleStatus.next_run && (
                  <p className="text-sm text-slate-500">
                    Next run: {new Date(scheduleStatus.next_run).toLocaleString()}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </motion.div>

      {/* Generate Reports */}
      <motion.div 
        className="swiss-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
            <FileText className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg text-slate-900">Generate Reports</h2>
            <p className="text-sm text-slate-500">Create health reports manually</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Trademark Selection */}
          <div>
            <Label className="text-sm font-semibold text-slate-700 uppercase tracking-wider block mb-2">
              Select Trade Mark
            </Label>
            <Select value={selectedTrademark} onValueChange={setSelectedTrademark}>
              <SelectTrigger className="input-swiss" data-testid="trademark-select">
                <SelectValue placeholder="Select trademark" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Trade Marks</SelectItem>
                {trademarks.map((tm) => (
                  <SelectItem key={tm.id} value={tm.id}>{tm.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Email Options */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Send Email</Label>
                <p className="text-xs text-slate-500">Send reports via email after generation</p>
              </div>
              <Switch
                checked={sendEmail}
                onCheckedChange={setSendEmail}
                data-testid="send-email-switch"
              />
            </div>

            {sendEmail && (
              <div className="flex items-center justify-between pl-4 border-l-2 border-slate-200">
                <div>
                  <Label className="text-sm font-semibold text-slate-700">Use Test Email</Label>
                  <p className="text-xs text-slate-500">Send to matthew@nbrg.co.uk instead of client emails</p>
                </div>
                <Switch
                  checked={useTestEmail}
                  onCheckedChange={setUseTestEmail}
                  data-testid="test-email-switch"
                />
              </div>
            )}
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerateReports}
            disabled={generating || trademarks.length === 0}
            className="btn-primary flex items-center gap-2"
            data-testid="generate-reports-btn"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            {generating ? 'GENERATING...' : 'GENERATE REPORTS'}
          </Button>
        </div>

        {/* Results */}
        {lastResults && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-800">{lastResults.message}</span>
            </div>
            
            {lastResults.details && lastResults.details.length > 0 && (
              <div className="space-y-2">
                {lastResults.details.map((detail, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{detail.trademark}</span>
                    <span className={`px-2 py-0.5 rounded ${getHealthColor(detail.score)}`}>
                      {detail.score}/100 - {detail.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Individual Reports */}
      <motion.div 
        className="swiss-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
            <Download className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg text-slate-900">Individual Reports</h2>
            <p className="text-sm text-slate-500">Preview or send reports for specific trade marks</p>
          </div>
        </div>

        {trademarks.length === 0 ? (
          <p className="text-slate-500 text-center py-8">
            No trade marks found. Add trade marks to generate health reports.
          </p>
        ) : (
          <div className="space-y-3">
            {trademarks.map((tm) => (
              <div 
                key={tm.id}
                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                data-testid={`trademark-row-${tm.id}`}
              >
                <div>
                  <p className="font-semibold text-slate-900">{tm.name}</p>
                  <p className="text-sm text-slate-500">{tm.owner || 'No owner set'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreviewReport(tm.id)}
                    className="flex items-center gap-1"
                    data-testid={`preview-${tm.id}`}
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendTestEmail(tm.id)}
                    className="flex items-center gap-1"
                    data-testid={`send-test-${tm.id}`}
                  >
                    <Send className="w-4 h-4" />
                    Test Email
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Configuration Note */}
      <motion.div 
        className="swiss-card bg-amber-50 border-amber-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-900">Email Configuration</p>
            <p className="text-sm text-amber-700 mt-1">
              To send emails to clients, configure the following environment variables on the server:
            </p>
            <ul className="text-sm text-amber-700 mt-2 list-disc list-inside">
              <li><code className="bg-amber-100 px-1 rounded">SENDGRID_API_KEY</code> - Your SendGrid API key</li>
              <li><code className="bg-amber-100 px-1 rounded">SENDER_EMAIL</code> - Email address to send from</li>
              <li><code className="bg-amber-100 px-1 rounded">TEST_EMAIL</code> - Test recipient (default: matthew@nbrg.co.uk)</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
